import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";

import type { ApiEndpoint, FlexibleDataItem } from "../../../types";
import DataItemCard from "./DataItemCard";
import { getSafeThumbnailUrl } from "../../../utilities/mediaUtils";
import { UI_CONSTANTS } from "../../../constants/uiConstants";
import ResultNotFound from "../ResultNotFound";
import useIntersectionObserver from "../../../hooks/useIntersectionObserver";
import StatusText from "./components/StatusText";
import { DataGridViewRendererProps } from "./DataGrid";
import DataItemCardSkeleton from "./components/GridViewSkeleton";

type EnhancedRelatedItem = FlexibleDataItem & {
  properties?: {
    relatedEndpoint?: ApiEndpoint;
    relatedCategory?: string;
    endpointColor?: string;
    [key: string]: any;
  };
};

interface DataGridViewProps extends DataGridViewRendererProps {
  data: FlexibleDataItem[];
  onSelect: (item: FlexibleDataItem) => void;
}

// Loading skeleton
function GridSkeleton({ gridCounts = 2 }: { gridCounts?: number }) {
  return Array.from({ length: gridCounts }).map((_, idx) => (
    <Grid item key={idx}>
      <DataItemCardSkeleton />
    </Grid>
  ));
}

export default function DataGridView({
  data = [],
  compact = false,
  currentEndpoint,
  onSelect,
  isPending,
  isAllDataFinishLoading = false,
  activeEntityId,
  onLoadMore,
  hasMoreToLoad,
  isMoreLoading,
  viewportVisibleIds,
  sx = {},
  ...restProps
}: DataGridViewProps) {
  const [itemsPerRow, setItemsPerRow] = useState(2);
  const parentRef = useRef<HTMLDivElement>(null);

  const { intersectElementRef } = useIntersectionObserver((isIntersect) => {
    if (isIntersect) {
      onLoadMore && onLoadMore();
    }
  });

  const dataRowCount = Math.ceil(data.length / itemsPerRow);
  const loaderRowCount = isMoreLoading ? 1 : 0; // Show extra row for loading skeleton when more data being fetching
  const totalRows = isPending ? 5 : isMoreLoading ? dataRowCount + 1 : dataRowCount + loaderRowCount; // On pending state show 5 rows as loading skeleton
  const rowVirtualizer = useVirtualizer({
    count: totalRows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (compact ? UI_CONSTANTS.LAYOUT.CARD_HEIGHT_COMPACT + 10 : UI_CONSTANTS.LAYOUT.CARD_HEIGHT_FULL + 4),
    overscan: 3
  });

  // Force recalculation of itemsPerRow when switching back to cards view
  const recalculateItemsPerRow = useCallback(() => {
    if (parentRef.current) {
      const containerWidth = parentRef.current.getBoundingClientRect().width;
      const minItemWidth = compact ? UI_CONSTANTS.LAYOUT.MIN_ITEM_WIDTH_COMPACT : UI_CONSTANTS.LAYOUT.MIN_ITEM_WIDTH_FULL;
      const newItemsPerRow = Math.floor(containerWidth / minItemWidth);
      setItemsPerRow(Math.max(1, newItemsPerRow));
    }
  }, [compact]);

  // Trigger recalculation when switching to cards view
  useEffect(() => {
    // Small delay to ensure the DOM has updated
    const timer = setTimeout(() => {
      recalculateItemsPerRow();
    }, 50);
    return () => clearTimeout(timer);
  }, [recalculateItemsPerRow]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      // Only calculate for cards view
      if (entries[0]) {
        const containerWidth = entries[0].contentRect.width;
        // Calculate items per row based on minimum item width
        const minItemWidth = compact ? UI_CONSTANTS.LAYOUT.MIN_ITEM_WIDTH_COMPACT : UI_CONSTANTS.LAYOUT.MIN_ITEM_WIDTH_FULL; // Smaller minimum width in compact mode
        const newItemsPerRow = Math.floor(containerWidth / minItemWidth);
        const calculatedItemsPerRow = Math.max(1, newItemsPerRow);
        setItemsPerRow(calculatedItemsPerRow);
      }
    });

    if (parentRef.current) {
      observer.observe(parentRef.current);
    }

    return () => observer.disconnect();
  }, [compact]);

  return (
    <Box
      component='div'
      {...restProps}
      ref={parentRef}
      className='scroller'
      sx={{
        position: "relative",
        height: "100dvh",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        overflow: "auto",
        ...sx
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: `${rowVirtualizer.getTotalSize()}px`,
          minHeight: "100%",
          boxSizing: "border-box",
          px: 24
        }}
      >
        {
          // Show Result not found message
          !isPending && (!data || data.length === 0) && <ResultNotFound sx={{ height: "calc(100dvh - 16rem)" }} />
        }
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const isLoaderRow = virtualRow.index === dataRowCount;
          const startIndex = virtualRow.index * itemsPerRow;
          const rowItems = data.slice(startIndex, startIndex + itemsPerRow);

          return (
            <Grid
              container
              gridColumn={12}
              spacing={compact ? 1 : 12}
              sx={{
                position: "absolute",
                marginInline: 0,
                px: 12,
                top: 0,
                transform: `translateY(${virtualRow.start}px)`,
                height: `${virtualRow.size}px`,
                left: 0,
                width: "100%",
                "& .MuiGrid-item": {
                  width: `${100 / itemsPerRow}%`
                }
              }}
              key={virtualRow.index}
            >
              {(isPending || isLoaderRow) && <GridSkeleton gridCounts={itemsPerRow} />}

              {!isPending &&
                !isLoaderRow &&
                rowItems.map((item) => {
                  const imageUrl = getSafeThumbnailUrl(item, currentEndpoint);

                  return (
                    <Grid
                      item
                      key={item.id}
                      sx={{
                        maxHeight: compact ? UI_CONSTANTS.LAYOUT.CARD_HEIGHT_COMPACT : UI_CONSTANTS.LAYOUT.CARD_HEIGHT_FULL,
                        px: 8,
                        opacity: viewportVisibleIds && !viewportVisibleIds.has(item.id) ? 0.4 : 1,
                        transition: "opacity 0.3s ease"
                      }}
                    >
                      <DataItemCard
                        compact={compact}
                        item={item}
                        imageUrl={imageUrl}
                        onSelect={onSelect}
                        endpoint={currentEndpoint}
                        borderColor={(item as EnhancedRelatedItem).properties?.endpointColor || undefined}
                        isActive={item.id === activeEntityId}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          );
        })}

        {/* End-of-list indicator */}
        {isAllDataFinishLoading && data?.length > 0 && !isMoreLoading && (
          <Box
            sx={{
              position: "absolute",
              top: rowVirtualizer.getTotalSize(),
              left: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              py: 2
            }}
          >
            <StatusText />
          </Box>
        )}
        {data?.length > 0 && hasMoreToLoad && !isMoreLoading && (
          // show sentinel only when not currently loading so resizing the drawer can re-trigger data fetch
          <Box
            component='div'
            ref={intersectElementRef}
            sx={{
              height: "1px",
              width: "100%",
              position: "absolute",
              top: rowVirtualizer.getTotalSize() - 24,
              left: 0
            }}
          />
        )}
      </Box>
    </Box>
  );
}
